import { useEffect } from 'react';
import { Eye, Ear, Hand, Brain, Monitor, Keyboard, MousePointer, Smartphone } from 'lucide-react';

const Accessibility = () => {
  useEffect(() => {
    document.title = 'Accessibility - FemSportNet';
  }, []);

  const features = [
    {
      icon: <Eye className="h-6 w-6 text-green-500" />,
      title: 'Visual Accessibility',
      description: 'High contrast modes, scalable text, and screen reader compatibility',
      details: [
        'WCAG 2.1 AA compliant color contrast ratios',
        'Scalable fonts up to 200% without horizontal scrolling',
        'Alternative text for all images and graphics',
        'Clear visual focus indicators for keyboard navigation'
      ]
    },
    {
      icon: <Ear className="h-6 w-6 text-green-500" />,
      title: 'Auditory Accessibility',
      description: 'Captions, transcripts, and visual alternatives for audio content',
      details: [
        'Closed captions for all video content',
        'Transcripts for audio and video materials',
        'Visual indicators for audio alerts',
        'No auto-playing audio content'
      ]
    },
    {
      icon: <Hand className="h-6 w-6 text-green-500" />,
      title: 'Motor Accessibility',
      description: 'Keyboard navigation and customizable interaction methods',
      details: [
        'Full keyboard navigation support',
        'Large click targets (minimum 44x44 pixels)',
        'Customizable timeout settings',
        'No content that requires precise timing'
      ]
    },
    {
      icon: <Brain className="h-6 w-6 text-green-500" />,
      title: 'Cognitive Accessibility',
      description: 'Clear language, consistent navigation, and error prevention',
      details: [
        'Simple, clear language and instructions',
        'Consistent navigation and layout',
        'Error prevention and clear error messages',
        'Multiple ways to find and access content'
      ]
    }
  ];

  const assistiveTech = [
    {
      icon: <Monitor className="h-6 w-6 text-blue-500" />,
      title: 'Screen Readers',
      description: 'Compatible with JAWS, NVDA, VoiceOver, and TalkBack',
      support: 'Full support'
    },
    {
      icon: <Keyboard className="h-6 w-6 text-blue-500" />,
      title: 'Keyboard Navigation',
      description: 'Complete functionality without mouse or touch',
      support: 'Full support'
    },
    {
      icon: <MousePointer className="h-6 w-6 text-blue-500" />,
      title: 'Voice Control',
      description: 'Works with Dragon NaturallySpeaking and Voice Control',
      support: 'Tested'
    },
    {
      icon: <Smartphone className="h-6 w-6 text-blue-500" />,
      title: 'Mobile Accessibility',
      description: 'Optimized for iOS and Android accessibility features',
      support: 'Full support'
    }
  ];

  const standards = [
    {
      name: 'WCAG 2.1 Level AA',
      description: 'Web Content Accessibility Guidelines compliance',
      status: 'Compliant'
    },
    {
      name: 'Section 508',
      description: 'US Federal accessibility requirements',
      status: 'Compliant'
    },
    {
      name: 'ADA',
      description: 'Americans with Disabilities Act compliance',
      status: 'Compliant'
    },
    {
      name: 'EN 301 549',
      description: 'European accessibility standard',
      status: 'Compliant'
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Accessibility Statement
            </h1>
            <p className="text-xl text-green-100 mb-8">
              FemSportNet is committed to ensuring digital accessibility for all users, including 
              those with disabilities. We strive to provide an inclusive experience for everyone.
            </p>
            <p className="text-green-100">
              Last updated: March 15, 2024
            </p>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Our Commitment
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                We believe that everyone should have equal access to information and functionality, 
                regardless of their abilities or the technologies they use.
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Accessibility Goals
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2">
                <li>Provide equal access to information and functionality for all users</li>
                <li>Ensure compatibility with assistive technologies</li>
                <li>Maintain clear and consistent navigation throughout the platform</li>
                <li>Offer multiple ways to access and interact with content</li>
                <li>Continuously improve accessibility based on user feedback</li>
                <li>Train our team on accessibility best practices</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Accessibility Features */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Accessibility Features
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white dark:bg-gray-700 rounded-lg shadow p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg mr-3">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {feature.title}
                    </h3>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    {feature.description}
                  </p>
                  
                  <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    {feature.details.map((detail, detailIndex) => (
                      <li key={detailIndex}>{detail}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Assistive Technology */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Assistive Technology Support
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {assistiveTech.map((tech, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
                  <div className="inline-flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 p-3 mb-4">
                    {tech.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {tech.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                    {tech.description}
                  </p>
                  <span className="inline-block bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-3 py-1 rounded-full text-xs font-medium">
                    {tech.support}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Standards Compliance */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Standards Compliance
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {standards.map((standard, index) => (
                <div key={index} className="bg-white dark:bg-gray-700 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {standard.name}
                    </h3>
                    <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-3 py-1 rounded-full text-sm font-medium">
                      {standard.status}
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">
                    {standard.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Keyboard Navigation */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Keyboard Navigation Guide
            </h2>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Navigation Keys
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono mr-3">Tab</kbd>
                      <span className="text-gray-600 dark:text-gray-300">Move to next element</span>
                    </div>
                    <div className="flex items-center">
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono mr-3">Shift + Tab</kbd>
                      <span className="text-gray-600 dark:text-gray-300">Move to previous element</span>
                    </div>
                    <div className="flex items-center">
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono mr-3">Enter</kbd>
                      <span className="text-gray-600 dark:text-gray-300">Activate buttons and links</span>
                    </div>
                    <div className="flex items-center">
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono mr-3">Space</kbd>
                      <span className="text-gray-600 dark:text-gray-300">Activate buttons and checkboxes</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Arrow Keys
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono mr-3">↑ ↓</kbd>
                      <span className="text-gray-600 dark:text-gray-300">Navigate menu items</span>
                    </div>
                    <div className="flex items-center">
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono mr-3">← →</kbd>
                      <span className="text-gray-600 dark:text-gray-300">Navigate tabs and sliders</span>
                    </div>
                    <div className="flex items-center">
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono mr-3">Esc</kbd>
                      <span className="text-gray-600 dark:text-gray-300">Close modals and menus</span>
                    </div>
                    <div className="flex items-center">
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono mr-3">Home/End</kbd>
                      <span className="text-gray-600 dark:text-gray-300">Jump to start/end of lists</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Known Issues */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Known Issues & Improvements
            </h2>
            
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow p-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Current Limitations
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 mb-6">
                <li>Some third-party embedded content may not be fully accessible</li>
                <li>Complex data visualizations are being enhanced with better descriptions</li>
                <li>Video player controls are being improved for better keyboard access</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Planned Improvements
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2">
                <li>Enhanced voice navigation support</li>
                <li>Improved mobile accessibility features</li>
                <li>Additional language support for screen readers</li>
                <li>Better customization options for visual preferences</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Feedback */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Accessibility Feedback
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              We welcome your feedback on the accessibility of FemSportNet. Please let us know 
              if you encounter any barriers or have suggestions for improvement.
            </p>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Contact Our Accessibility Team
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Email</h4>
                  <p className="text-gray-600 dark:text-gray-300">accessibility@femsportnet.com</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Phone</h4>
                  <p className="text-gray-600 dark:text-gray-300">+1 (555) 123-4567</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Response Time</h4>
                  <p className="text-gray-600 dark:text-gray-300">Within 2 business days</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Alternative Formats</h4>
                  <p className="text-gray-600 dark:text-gray-300">Available upon request</p>
                </div>
              </div>
            </div>
            
            <div className="mt-8">
              <button className="bg-green-500 hover:bg-green-600 text-white font-medium px-8 py-3 rounded-lg transition-colors duration-200">
                Report Accessibility Issue
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Accessibility;